import { useEffect, useState, useCallback, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Modal, Select } from './index';
import { AlertCircleIcon, EditIcon, LinkIcon, ReplyIcon } from './IconSet';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { API_BASE } from '../config';
import './CommentSection.css';

const EDIT_WINDOW_MS = 15 * 60 * 1000;

const REPORT_REASONS = [
  { value: 'abusive', label: 'Abusive or offensive' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'spam', label: 'Spam' },
  { value: 'fraudulent', label: 'Fraudulent / misleading' },
  { value: 'hate', label: 'Hate or discrimination' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'other', label: 'Other' },
];

function initials(name) {
  return (name || '?').trim()[0]?.toUpperCase() || '?';
}

function timeAgo(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' });
}

function Composer({ value, onChange, onSubmit, onCancel, submitting, placeholder, autoFocus }) {
  return (
    <div className="comment-composer">
      <textarea
        className="comment-composer__textarea"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={1}
        maxLength={2000}
        autoFocus={autoFocus}
      />
      <div className="comment-composer__actions">
        {onCancel && (
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        )}
        <Button size="sm" onClick={onSubmit} disabled={submitting || !value.trim()}>
          {submitting ? 'Posting…' : 'Post comment'}
        </Button>
      </div>
    </div>
  );
}

export default function CommentSection({ postId }) {
  const navigate = useNavigate();
  const { user, authedFetch } = useAuth();
  const { showToast } = useToast();

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyingToId, setReplyingToId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState('abusive');
  const [reportDescription, setReportDescription] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportDone, setReportDone] = useState(false);

  const [highlightedId, setHighlightedId] = useState(null);
  const hashHandledRef = useRef(false);

  const load = useCallback(() => {
    setLoading(true);
    authedFetch(`${API_BASE}/blog-posts/${postId}/comments`)
      .then((res) => (res.ok ? res.json() : { comments: [] }))
      .then((data) => setComments(data.comments || []))
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  }, [authedFetch, postId]);

  // Deep link to a single comment (?#comment-<id>), same idea as a Jira
  // work item / comment permalink - scroll to it and briefly highlight it,
  // once, the first time comments finish loading.
  useEffect(() => {
    if (loading || hashHandledRef.current) return;
    const match = window.location.hash.match(/^#comment-(\d+)$/);
    if (!match) return;
    // comment.id can come back as a string (Postgres BIGINT serialization)
    // or a number (SQLite) - compare as strings so either shape matches.
    const id = match[1];
    if (!comments.some((c) => String(c.id) === id)) return;
    hashHandledRef.current = true;
    document.getElementById(`comment-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedId(id);
    const timer = setTimeout(() => setHighlightedId(null), 2500);
    return () => clearTimeout(timer);
  }, [loading, comments]);

  const copyCommentLink = async (commentId) => {
    const url = `${window.location.origin}${window.location.pathname}#comment-${commentId}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast('Link copied to clipboard.', 'success');
    } catch {
      showToast('Could not copy the link.', 'error');
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const topLevel = comments.filter((c) => !c.parent_comment_id);
  const repliesFor = (id) => comments.filter((c) => c.parent_comment_id === id);
  const publishedCount = comments.filter((c) => c.status === 'published').length;

  const postComment = async (body, parentCommentId, onDone) => {
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      const res = await authedFetch(`${API_BASE}/blog-posts/${postId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ body: body.trim(), parentCommentId: parentCommentId || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to post comment.');
      showToast(data.status === 'pending' ? 'Submitted for review.' : 'Comment posted.', 'success');
      onDone();
      load();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const saveEdit = async (commentId) => {
    if (!editText.trim()) return;
    setSubmitting(true);
    try {
      const res = await authedFetch(`${API_BASE}/blog-posts/${postId}/comments/${commentId}`, {
        method: 'PATCH',
        body: JSON.stringify({ body: editText.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update comment.');
      showToast('Comment updated.', 'success');
      setEditingId(null);
      load();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteComment = async (commentId) => {
    try {
      const res = await authedFetch(`${API_BASE}/blog-posts/${postId}/comments/${commentId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete comment.');
      showToast('Comment deleted.', 'success');
      load();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const submitReport = async (e) => {
    e.preventDefault();
    setReportSubmitting(true);
    try {
      const res = await authedFetch(`${API_BASE}/blog-posts/${postId}/comments/${reportTarget}/report`, {
        method: 'POST',
        body: JSON.stringify({ reason: reportReason, description: reportDescription.trim() || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to submit report.');
      setReportDone(true);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setReportSubmitting(false);
    }
  };

  const canEdit = (comment) =>
    user &&
    Number(comment.author_id) === Number(user.id) &&
    ['pending', 'published', 'flagged'].includes(comment.status) &&
    Date.now() - new Date(comment.created_at).getTime() < EDIT_WINDOW_MS;

  const canDelete = (comment) => user && Number(comment.author_id) === Number(user.id) && comment.status !== 'removed';

  const renderComment = (comment, isReply) => {
    if (comment.status === 'removed') {
      return (
        <div key={comment.id} className={`comment${isReply ? ' comment--reply' : ''}`}>
          <div className="comment__avatar comment__avatar--fallback">–</div>
          <div className="comment__body">
            <p className="comment__text comment__text--removed">[Comment removed]</p>
          </div>
        </div>
      );
    }

    const isEditing = editingId === comment.id;
    const isPending = comment.status === 'pending' || comment.status === 'flagged';

    return (
      <div
        key={comment.id}
        id={`comment-${comment.id}`}
        className={`comment${isReply ? ' comment--reply' : ''}${isPending ? ' comment--pending' : ''}${highlightedId !== null && String(comment.id) === highlightedId ? ' comment--highlighted' : ''}`}
      >
        {comment.author_avatar_url ? (
          <img className="comment__avatar" src={comment.author_avatar_url} alt="" />
        ) : (
          <div className="comment__avatar comment__avatar--fallback">{initials(comment.author_name)}</div>
        )}
        <div className="comment__body">
          <div className="comment__meta">
            <button type="button" className="comment__name" onClick={() => navigate(`/seller/${comment.author_id}`)}>
              {comment.author_name || 'NaijaNest member'}
            </button>
            {comment.is_contributor && <span className="comment__role">Contributor</span>}
            <span className="comment__time">· {timeAgo(comment.created_at)}</span>
            {comment.edited_at && <span className="comment__edited">(edited)</span>}
            {isPending && <Badge variant="warning">Pending review</Badge>}
          </div>

          {isEditing ? (
            <div className="comment__edit-box">
              <textarea
                className="comment-composer__textarea"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={2}
                maxLength={2000}
              />
              <div className="comment-composer__actions">
                <Button variant="secondary" size="sm" onClick={() => setEditingId(null)} disabled={submitting}>
                  Cancel
                </Button>
                <Button size="sm" onClick={() => saveEdit(comment.id)} disabled={submitting}>
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <p className="comment__text">{comment.body}</p>
          )}

          {!isEditing &&
            (() => {
              const actionItems = [];
              actionItems.push({
                key: 'copy-link',
                icon: <LinkIcon size={11} />,
                label: 'Copy link',
                onClick: () => copyCommentLink(comment.id),
              });
              if (!isReply && user) {
                actionItems.push({
                  key: 'reply',
                  icon: <ReplyIcon size={11} />,
                  label: 'Reply',
                  onClick: () => {
                    setReplyingToId(replyingToId === comment.id ? null : comment.id);
                    setReplyText('');
                  },
                });
              }
              if (canEdit(comment)) {
                actionItems.push({
                  key: 'edit',
                  icon: <EditIcon size={11} />,
                  label: 'Edit',
                  onClick: () => {
                    setEditingId(comment.id);
                    setEditText(comment.body);
                  },
                });
              }
              if (canDelete(comment)) {
                actionItems.push({
                  key: 'delete',
                  label: 'Delete',
                  onClick: () => deleteComment(comment.id),
                  className: 'comment__action--report',
                });
              }
              if (user && Number(comment.author_id) !== Number(user.id)) {
                actionItems.push({
                  key: 'report',
                  icon: <AlertCircleIcon size={11} />,
                  label: 'Report',
                  onClick: () => {
                    setReportDone(false);
                    setReportDescription('');
                    setReportTarget(comment.id);
                  },
                  className: 'comment__action--report',
                });
              }
              if (actionItems.length === 0) return null;
              return (
                <div className="comment__actions">
                  {actionItems.map((item, i) => (
                    <Fragment key={item.key}>
                      {i > 0 && <span className="comment__action-divider" />}
                      <button
                        type="button"
                        className={`comment__action${item.className ? ` ${item.className}` : ''}`}
                        onClick={item.onClick}
                      >
                        {item.icon}
                        {item.label}
                      </button>
                    </Fragment>
                  ))}
                </div>
              );
            })()}

          {replyingToId === comment.id && (
            <div className="comment__reply-composer">
              <Composer
                value={replyText}
                onChange={setReplyText}
                onCancel={() => setReplyingToId(null)}
                onSubmit={() =>
                  postComment(replyText, comment.id, () => {
                    setReplyingToId(null);
                    setReplyText('');
                  })
                }
                submitting={submitting}
                placeholder={`Reply to ${comment.author_name || 'this comment'}…`}
                autoFocus
              />
            </div>
          )}

          {!isReply && repliesFor(comment.id).length > 0 && (
            <div className="comment__replies">{repliesFor(comment.id).map((r) => renderComment(r, true))}</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="comment-section">
      <div className="comment-section__head">
        <h2>Join the Discussion</h2>
        <span>{publishedCount} comment{publishedCount === 1 ? '' : 's'}</span>
      </div>

      {user ? (
        <Composer
          value={newComment}
          onChange={setNewComment}
          onSubmit={() => postComment(newComment, null, () => setNewComment(''))}
          submitting={submitting}
          placeholder="Share your thoughts on this article…"
        />
      ) : (
        <div className="comment-section__signin">
          <span>Sign in to join the discussion.</span>
          <Button onClick={() => navigate('/login', { state: { from: window.location.pathname } })}>Sign in</Button>
        </div>
      )}

      {loading ? (
        <p className="admin-muted">Loading comments…</p>
      ) : topLevel.length === 0 ? (
        <p className="admin-muted comment-section__empty">Be the first to comment.</p>
      ) : (
        <div className="comment-list">{topLevel.map((c) => renderComment(c, false))}</div>
      )}

      <Modal
        isOpen={reportTarget !== null}
        onClose={() => setReportTarget(null)}
        title="Report this comment"
      >
        {reportDone ? (
          <>
            <Alert type="success">Thank you, your report has been submitted for review.</Alert>
            <div className="admin-modal-actions">
              <Button variant="secondary" onClick={() => setReportTarget(null)}>
                Close
              </Button>
            </div>
          </>
        ) : (
          <form onSubmit={submitReport}>
            <label className="property-report-label" htmlFor="comment-report-reason">
              Reason for reporting
            </label>
            <Select
              id="comment-report-reason"
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              options={REPORT_REASONS}
            />
            <label className="property-report-label" htmlFor="comment-report-details">
              Additional details <span className="admin-muted">(optional)</span>
            </label>
            <textarea
              id="comment-report-details"
              className="property-report-textarea"
              rows={3}
              maxLength={500}
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
            />
            <div className="property-report-actions">
              <Button type="submit" disabled={reportSubmitting}>
                {reportSubmitting ? 'Submitting…' : 'Submit report'}
              </Button>
              <Button type="button" variant="secondary" disabled={reportSubmitting} onClick={() => setReportTarget(null)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
